import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { ConfirmationService, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';

import { HttpService } from 'src/app/services/http.service';
import {
  ageEligibilityEstablished,
  canWaiveRequirement,
  isWaivedRequirement,
  requirementSatisfiesApprovalGate,
} from './onboarding-waiver';
import { coerceOptionalNumber, resolveApiErrorMessage } from 'src/app/utils/api-error-message.util';
import { environment } from 'src/environments/environment';

interface DeliveryPartner {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  photoUrl?: string | null;
  email?: string;
  firebaseEmail?: string;
  onboardingOverallStatus?: string;
  status: string;
  accessStatus?: string;
  platformLevel?: number;
  rating?: number | null;
  completedDeliveries?: number;
  cancelledDeliveries?: number;
  incidentCount?: number;
  rateMinPrice?: number;
  ratePerAdditionalKm?: number;
  cashCapacityAmount?: number;
  qaResetEligible?: boolean;
  createdOn?: string;
  /** Fields added for list search, filter, and sort. */
  listName?: string;
  listEmail?: string;
  accessLabel?: string;
  onboardingLabelText?: string;
  phoneDigits?: string;
  searchText?: string;
  ratingValue?: number | null;
}

interface OnboardingArtifact {
  artifactId: string;
  artifactType?: string;
  reviewStatus?: string;
  reviewStatusLabelEs?: string;
  mimeType?: string;
  sizeBytes?: number;
  originalFilename?: string;
  videoChallengeWords?: string;
  correctionReason?: string;
  contentPath?: string;
  contentAvailable?: boolean;
  current?: boolean;
  createdAt?: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  previewUrl?: string;
  previewFailed?: boolean;
  previewLoading?: boolean;
  /** Last preview/download failure detail for operators (not shown to couriers). */
  previewError?: string;
}

interface RequirementGroup {
  requirementKey: string;
  labelEs?: string;
  required?: boolean;
  currentStatus?: string;
  reviewStatus?: string;
  correctionReason?: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  administrativeDecision?: string | null;
  waiverReason?: string | null;
  waivedBy?: string | null;
  waivedAt?: string | null;
  currentArtifact?: OnboardingArtifact;
  historyArtifacts?: OnboardingArtifact[];
}

interface OnboardingSummary {
  overallStatus?: string;
  submittedAt?: string;
  reviewedAt?: string;
  correctionMessage?: string;
  accessDecisionReason?: string;
  applicantRejectionExplanation?: string;
  steps?: Array<{
    stepId?: string;
    status?: string;
    required?: boolean;
    blocking?: boolean;
    correctionReason?: string;
  }>;
  documentReview?: {
    pendingReviewCount?: number;
    needsCorrectionCount?: number;
    approvedCount?: number;
  };
}

interface OnboardingReview {
  partnerId?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  address?: string;
  dateOfBirth?: string;
  age?: number;
  overallStatusLabelEs?: string;
  submittedAt?: string;
  lastUpdatedAt?: string;
  onboarding?: OnboardingSummary;
  artifacts?: OnboardingArtifact[];
  requirementGroups?: RequirementGroup[];
}

interface HubCourierReviewItem {
  id?: string;
  hubId?: string;
  hubName?: string | null;
  stars?: number;
  comment?: string | null;
  createdAt?: string | null;
}

interface HubCourierPublicProfile {
  deliveryPartnerId?: string;
  displayName?: string | null;
  averageRating?: number | null;
  reviewCount?: number;
  reviews?: HubCourierReviewItem[];
}

interface NewPartnerForm {
  displayName: string;
  phone: string;
  email: string;
  rateMinPrice: number | null;
  ratePerAdditionalKm: number | null;
  cashCapacityAmount: number | null;
}

type RejectTarget =
  | { kind: 'artifact'; group: RequirementGroup; artifact: OnboardingArtifact }
  | { kind: 'step'; stepId: string; labelEs: string };

/**
 * TinyAll Delivery Network — admin management of delivery partners.
 * Create → issue device token → courier enters token in app → approve access → owner pairs at hub.
 */
@Component({
  selector: 'app-delivery-partners',
  templateUrl: './delivery-partners.component.html',
  styleUrls: ['./delivery-partners.component.scss'],
})
export class DeliveryPartnersComponent implements OnInit {
  @ViewChild('partnersTable') partnersTable?: Table;
  readonly apiUrl = environment.apiUrl;
  private readonly baseUrl = `${environment.apiUrl}/admin-center/delivery-network/v1`;

  private readonly hiddenRequirementKeys = new Set([
    'APPLICATION_SUBMIT',
    'DELIVERY_RATE',
    'CASH_CAPACITY',
    'PERMISSIONS_ACK',
  ]);

  private readonly artifactRequirementKeys = new Set([
    'IDENTITY_DOCUMENT_FRONT',
    'IDENTITY_DOCUMENT_BACK',
    'IDENTITY_VIDEO',
    'PROFILE_PHOTO',
  ]);

  private readonly quickReasonsByKey: Record<string, string[]> = {
    IDENTITY_DOCUMENT_FRONT: [
      'La imagen está borrosa.',
      'No se ve el documento completo.',
      'Hay reflejos o poca iluminación.',
      'No corresponde a la Cédula frontal.',
      'La información no es legible.',
      'Otro.',
    ],
    IDENTITY_DOCUMENT_BACK: [
      'La imagen está borrosa.',
      'No se ve el documento completo.',
      'Hay reflejos o poca iluminación.',
      'No corresponde al reverso de la Cédula.',
      'La información no es legible.',
      'Otro.',
    ],
    IDENTITY_VIDEO: [
      'No se ve claramente el rostro.',
      'No se escucha el audio.',
      'No leyó las palabras indicadas.',
      'El video está incompleto.',
      'La iluminación es insuficiente.',
      'Otro.',
    ],
    PROFILE_PHOTO: [
      'El rostro no se ve claro.',
      'La foto está borrosa.',
      'La iluminación es insuficiente.',
      'La persona usa gafas oscuras o el rostro está cubierto.',
      'La foto no corresponde a una foto de perfil.',
      'Otro.',
    ],
    PERSONAL_INFO: [
      'Nombre o apellido incompleto.',
      'WhatsApp inválido.',
      'Dirección incompleta.',
      'Fecha de nacimiento inválida.',
      'No cumple con la edad mínima.',
      'Otro.',
    ],
  };

  private previewUrls: string[] = [];

  /** Fullscreen preview of the currently selected artifact blob. */
  lightboxVisible = false;
  lightboxArtifact: OnboardingArtifact | null = null;

  /** Every courier returned by the admin API, before the on-screen filters. */
  allPartners: DeliveryPartner[] = [];
  data: DeliveryPartner[] = [];
  isLoading = false;
  featureDisabled = false;
  listTruncated = false;
  pageSizeOptions: number[] = [15, 30, 60, 100];

  readonly partnerStatuses = [
    'PENDING',
    'APPROVED',
    'SHORTLISTED',
    'PAUSED',
    'REVIEW_HOLD',
    'SUSPENDED',
    'DEACTIVATED',
    'REJECTED',
  ];
  readonly statusOptions = ['', ...this.partnerStatuses];
  readonly accessOptions = [
    { value: '', label: 'All access' },
    { value: 'APPLICANT', label: 'Applicant' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'SHORTLISTED', label: 'Shortlisted' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'REVIEW_HOLD', label: 'Review hold' },
    { value: 'SUSPENDED', label: 'Suspended' },
    { value: 'DEACTIVATED', label: 'Deactivated' },
    { value: 'REJECTED', label: 'Rejected' },
  ];
  readonly onboardingOptions = [
    { value: '', label: 'All onboarding' },
    { value: 'NEEDS_REVIEW', label: 'Needs review' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under review' },
    { value: 'NEEDS_CORRECTION', label: 'Needs correction' },
    { value: 'IN_PROGRESS', label: 'In progress' },
    { value: 'NOT_STARTED', label: 'Not started' },
    { value: 'APPROVED', label: 'Onboarding approved' },
    { value: 'REJECTED', label: 'Onboarding rejected' },
  ];
  readonly statusColumnOptions = this.partnerStatuses.map((value) => ({ label: value, value }));
  readonly onboardingColumnOptions = this.onboardingOptions
    .filter((option) => option.value && option.value !== 'NEEDS_REVIEW')
    .map((option) => ({ label: option.label, value: option.label }));
  readonly accessColumnOptions = this.accessOptions
    .filter((option) => option.value)
    .map((option) => ({ label: option.label, value: option.value }));
  statusFilter = '';
  onboardingFilter = '';
  accessFilter = '';
  search = '';

  createDialogVisible = false;
  isSaving = false;
  /** Prevents double-submit on approve/suspend/reject status mutations. */
  statusUpdating = false;
  newPartner: NewPartnerForm = this.emptyForm();

  tokenDialogVisible = false;
  issuedToken = '';
  issuedTokenPartnerName = '';

  onboardingDialogVisible = false;
  reviewsDialogVisible = false;
  reviewsLoading = false;
  reviewsPartnerName = '';
  courierReviewProfile: HubCourierPublicProfile | null = null;
  onboardingLoading = false;
  onboardingPartnerName = '';
  onboardingReview: OnboardingReview | null = null;
  selectedPartner: DeliveryPartner | null = null;
  missingApprovals: string[] = [];

  rejectDialogVisible = false;
  applicationRejectVisible = false;
  applicationRejectPartner: DeliveryPartner | null = null;
  applicantExplanation = '';
  internalRejectionNote = '';
  rejectTarget: RejectTarget | null = null;
  rejectReason = '';
  rejectSubmitting = false;
  waiverDialogVisible = false;
  waiverAction: 'WAIVE' | 'REVOKE' = 'WAIVE';
  waiverTarget: RequirementGroup | null = null;
  waiverReason = '';
  demotionDialogVisible = false;
  demotionPartner: DeliveryPartner | null = null;
  demotionReason = '';
  applicantReturnVisible = false;
  applicantReturnPartner: DeliveryPartner | null = null;
  applicantReturnReason = '';

  reviewActionKey = '';
  expandedHistoryKeys = new Set<string>();
  historyShowAllKeys = new Set<string>();

  constructor(
    private httpService: HttpService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer
  ) {}

  /** Angular marks blob: URLs as unsafe in [src] bindings; trust URLs we created ourselves. */
  trustPreviewUrl(url?: string | null): SafeUrl | null {
    if (!url) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const pageSize = 200;
    const maxPages = 50;
    const rows: DeliveryPartner[] = [];
    const seen = new Set<string>();
    this.listTruncated = false;
    try {
      for (let page = 0; page < maxPages; page += 1) {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(pageSize));
        const response = await this.httpService
          .doGet(`${this.baseUrl}/partners?${params.toString()}`)
          .toPromise();
        const batch = Array.isArray(response) ? (response as DeliveryPartner[]) : [];
        const fresh = batch.filter((partner) => partner?.id && !seen.has(partner.id));
        fresh.forEach((partner) => seen.add(partner.id));
        rows.push(...fresh.map((partner) => this.prepareListRow(partner)));
        if (fresh.length === 0 || batch.length < pageSize) {
          break;
        }
        if (page === maxPages - 1) {
          this.listTruncated = true;
        }
      }
      this.allPartners = rows;
      this.pageSizeOptions = rows.length > 100 ? [15, 30, 60, 100, rows.length] : [15, 30, 60, 100];
      this.applyView();
      this.featureDisabled = false;
    } catch (error: any) {
      this.allPartners = [];
      this.data = [];
      if (error?.status === 404) {
        this.featureDisabled = true;
      } else {
        this.toastError('Could not load delivery partners');
      }
    } finally {
      this.isLoading = false;
    }
  }

  applyView(): void {
    const term = this.search.trim().toLowerCase();
    const digits = term.replace(/\D/g, '');
    const onboarding = this.onboardingFilter;
    this.data = this.allPartners.filter((partner) => {
      if (this.statusFilter && partner.status !== this.statusFilter) {
        return false;
      }
      if (this.accessFilter && partner.accessLabel !== this.accessFilter) {
        return false;
      }
      if (onboarding === 'NEEDS_REVIEW') {
        if (!['SUBMITTED', 'UNDER_REVIEW', 'NEEDS_CORRECTION'].includes(partner.onboardingOverallStatus || '')) {
          return false;
        }
      } else if (onboarding && partner.onboardingOverallStatus !== onboarding) {
        return false;
      }
      if (!term) {
        return true;
      }
      if ((partner.searchText || '').includes(term)) {
        return true;
      }
      return digits.length >= 3 && (partner.phoneDigits || '').includes(digits);
    });
  }

  clearListFilters(): void {
    this.search = '';
    this.statusFilter = '';
    this.onboardingFilter = '';
    this.accessFilter = '';
    this.partnersTable?.clear();
    this.applyView();
  }

  onboardingLabel(partner: DeliveryPartner): string {
    const match = this.onboardingOptions.find((option) => option.value === partner.onboardingOverallStatus);
    return match?.label || partner.onboardingOverallStatus || 'Not started';
  }

  private prepareListRow(partner: DeliveryPartner): DeliveryPartner {
    const first = (partner.firstName || '').trim();
    const last = (partner.lastName || '').trim();
    const combinedName = `${first} ${last}`.trim();
    const listName = (partner.displayName || combinedName || '—').trim();
    const listEmail = (partner.email || partner.firebaseEmail || '').trim();
    const phoneDigits = String(partner.phone || '').replace(/\D/g, '');
    const ratingValue = partner.rating == null || !Number.isFinite(Number(partner.rating))
      ? null
      : Number(partner.rating);
    const row: DeliveryPartner = {
      ...partner,
      listName,
      listEmail,
      phoneDigits,
      ratingValue,
      accessLabel: this.displayAccessStatus(partner),
      onboardingLabelText: '',
      platformLevel: Number(partner.platformLevel || 0),
      completedDeliveries: Number(partner.completedDeliveries || 0),
      incidentCount: Number(partner.incidentCount || 0),
    };
    row.onboardingLabelText = this.onboardingLabel(row);
    row.searchText = [listName, first, last, combinedName, partner.email, partner.firebaseEmail, partner.phone, phoneDigits, partner.id]
      .filter((value) => !!value)
      .join(' ')
      .toLowerCase();
    return row;
  }

  openCreateDialog(): void {
    this.newPartner = this.emptyForm();
    this.createDialogVisible = true;
  }

  async saveNewPartner(): Promise<void> {
    if (!this.newPartner.displayName?.trim() || !this.newPartner.phone?.trim()) {
      this.toastError('Display name and phone are required');
      return;
    }

    this.isSaving = true;
    try {
      await this.httpService
        .doPost(`${this.baseUrl}/partners`, {
          displayName: this.newPartner.displayName.trim(),
          phone: this.newPartner.phone.trim(),
          email: this.newPartner.email?.trim() || null,
          rateMinPrice: coerceOptionalNumber(this.newPartner.rateMinPrice),
          ratePerAdditionalKm: coerceOptionalNumber(this.newPartner.ratePerAdditionalKm),
          cashCapacityAmount: coerceOptionalNumber(this.newPartner.cashCapacityAmount),
        })
        .toPromise();

      this.toastSuccess('Delivery partner created (PENDING). Issue a device token, then approve after onboarding.');
      this.createDialogVisible = false;
      this.loadData();
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'Could not create delivery partner'));
    } finally {
      this.isSaving = false;
    }
  }

  confirmSetStatus(partner: DeliveryPartner, status: string): void {
    if (status === 'REJECTED') {
      this.applicationRejectPartner = partner;
      this.applicantExplanation = '';
      this.internalRejectionNote = '';
      this.applicationRejectVisible = true;
      return;
    }
    const notes: Record<string, string> = {
      APPROVED: 'Full network access (paired hubs + open-network broadcasts).',
      SHORTLISTED:
        'Paired-hub only: can be paired and take paired offers, but not open-network broadcasts.',
      SUSPENDED: 'Blocks all courier work.',
      REJECTED: 'Rejects the application.',
    };
    const note = notes[status] ? ` ${notes[status]}` : '';
    this.confirmationService.confirm({
      message: `Set ${partner.displayName} to ${status}?${note}`,
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.setStatus(partner, status),
    });
  }

  confirmQaReset(partner: DeliveryPartner): void {
    const key = this.newQaResetKey();
    this.confirmationService.confirm({
      message:
        `Esto inicia un ciclo de solicitud nuevo para ${partner.displayName}. ` +
        'La cuenta de Google se conserva. Se limpia la solicitud, la aprobación, los emparejamientos, ' +
        'las tarifas, el efectivo, la disponibilidad y la sesión de la app. ' +
        'Si tiene una entrega asignada, se le retira y el pedido del Hub queda igual. ' +
        'El historial del repartidor empieza vacío.',
      header: 'Reset QA courier',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.resetQaCourier(partner, key),
    });
  }

  private async resetQaCourier(partner: DeliveryPartner, idempotencyKey: string): Promise<void> {
    try {
      await this.httpService
        .doPost(`${this.baseUrl}/partners/${encodeURIComponent(partner.id)}/qa-reset`, { idempotencyKey })
        .toPromise();
      this.toastSuccess(`${partner.displayName} debe iniciar sesión de nuevo y completar la solicitud.`);
      void this.loadData();
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'No se pudo reiniciar el repartidor de QA.'));
    }
  }

  private newQaResetKey(): string {
    const webCrypto = (typeof crypto !== 'undefined' ? crypto : null) as { randomUUID?: () => string } | null;
    if (webCrypto && typeof webCrypto.randomUUID === 'function') {
      return webCrypto.randomUUID();
    }
    return `qa-reset-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  /** List-row Approve: PENDING opens review; SHORTLISTED restores; others confirm. */
  confirmApproveFromList(partner: DeliveryPartner): void {
    const status = (partner.status || '').toUpperCase();
    if (status === 'PENDING') {
      void this.viewOnboarding(partner);
      this.messageService.add({
        severity: 'info',
        summary: 'Revisión requerida',
        detail: 'Aprueba cada elemento y luego usa «Aprobar solicitud».',
      });
      return;
    }
    if (status === 'SHORTLISTED') {
      this.confirmationService.confirm({
        message:
          `¿Reactivar a ${partner.displayName} como APPROVED (acceso completo a la red)?`,
        header: 'Reactivar',
        icon: 'pi pi-check-circle',
        accept: () => this.setStatus(partner, 'APPROVED'),
      });
      return;
    }
    this.confirmSetStatus(partner, 'APPROVED');
  }

  approveButtonLabel(partner: DeliveryPartner): string {
    return (partner.status || '').toUpperCase() === 'SHORTLISTED' ? 'Reactivar' : 'Approve';
  }

  confirmShortlist(partner: DeliveryPartner): void {
    if ((partner.status || '').toUpperCase() === 'APPROVED') {
      this.demotionPartner = partner;
      this.demotionReason = '';
      this.demotionDialogVisible = true;
      return;
    }
    const reviewingThisPartner =
      this.selectedPartner?.id === partner.id && this.displayRequirementGroups().length > 0;
    if (reviewingThisPartner && !this.canApproveApplicant()) {
      this.toastError('No puedes preseleccionar esta solicitud todavía.');
      return;
    }
    this.confirmSetStatus(partner, 'SHORTLISTED');
  }

  openApplicantReturn(partner: DeliveryPartner): void {
    this.applicantReturnPartner = partner;
    this.applicantReturnReason = '';
    this.applicantReturnVisible = true;
  }

  async submitApplicantReturn(): Promise<void> {
    const partner = this.applicantReturnPartner;
    if (!partner || this.statusUpdating) {
      return;
    }
    const reason = this.applicantReturnReason.trim();
    const fromReview = this.onboardingDialogVisible && this.selectedPartner?.id === partner.id;
    this.applicantReturnVisible = false;
    await this.setStatus(partner, 'APPLICANT', fromReview, reason || undefined);
  }

  async submitApprovedDemotion(): Promise<void> {
    const partner = this.demotionPartner;
    const reason = this.demotionReason.trim();
    if (!partner || !reason) {
      this.toastError('A reason is required to restrict an approved courier to shortlisted.');
      return;
    }
    const fromReview = this.onboardingDialogVisible && this.selectedPartner?.id === partner.id;
    this.demotionDialogVisible = false;
    await this.setStatus(partner, 'SHORTLISTED', fromReview, reason);
  }

  confirmApproveApplicant(): void {
    if (!this.selectedPartner) {
      return;
    }
    const restoringShortlist =
      (this.selectedPartner.status || '').toUpperCase() === 'SHORTLISTED';
    if (!this.canApproveApplicant()) {
      this.toastError('No puedes aprobar esta solicitud todavía.');
      return;
    }
    const name = this.onboardingPartnerName || this.selectedPartner.displayName;
    this.confirmationService.confirm({
      message: restoringShortlist
        ? `¿Reactivar a ${name} como APPROVED (acceso completo a la red)?`
        : `¿Aprobar la solicitud de ${name}?`,
      header: restoringShortlist ? 'Reactivar' : 'Aprobar solicitud',
      icon: 'pi pi-check-circle',
      accept: () => this.approveApplicant(),
    });
  }

  approveFooterLabel(): string {
    return (this.selectedPartner?.status || '').toUpperCase() === 'SHORTLISTED'
      ? 'Reactivar a aprobado'
      : 'Aprobar solicitud';
  }

  /** Footer primary action uses the same document checklist for shortlist and approval. */
  canSubmitApproveFooter(): boolean {
    if (!this.selectedPartner) {
      return false;
    }
    return this.canApproveApplicant();
  }

  private async approveApplicant(): Promise<void> {
    if (!this.selectedPartner) {
      return;
    }
    await this.setStatus(this.selectedPartner, 'APPROVED', true);
  }

  submitApplicationReject(): void {
    const partner = this.applicationRejectPartner;
    if (!partner || this.statusUpdating) {
      return;
    }
    const explanation = this.applicantExplanation.trim();
    const note = this.internalRejectionNote.trim();
    this.applicationRejectVisible = false;
    this.setStatus(
      partner,
      'REJECTED',
      this.onboardingDialogVisible,
      note || undefined,
      explanation || undefined
    );
  }

  private async setStatus(
    partner: DeliveryPartner,
    status: string,
    fromReviewModal = false,
    reason?: string,
    applicantExplanation?: string
  ): Promise<void> {
    if (this.statusUpdating) {
      return;
    }
    const accessStatus = this.mapLegacyStatusToAccess(status);
    this.statusUpdating = true;
    try {
      if (accessStatus) {
        const body: { accessStatus: string; reason?: string; applicantExplanation?: string } = { accessStatus };
        if (reason && reason.trim()) {
          body.reason = reason.trim();
        }
        if (applicantExplanation && applicantExplanation.trim()) {
          body.applicantExplanation = applicantExplanation.trim();
        }
        await this.httpService
          .doPatch(`${this.baseUrl}/partners/${encodeURIComponent(partner.id)}/access-status`, body)
          .toPromise();
      } else {
        await this.httpService
          .doPatch(`${this.baseUrl}/partners/${encodeURIComponent(partner.id)}/status`, { status })
          .toPromise();
      }

      this.toastSuccess(`Partner set to ${status}`);
      if (fromReviewModal) {
        this.onboardingDialogVisible = false;
      }
      this.loadData();
    } catch (error: any) {
      if (error?.status === 409 && Array.isArray(error?.error?.missingApprovals)) {
        this.missingApprovals = error.error.missingApprovals;
        this.toastError(error.error.details || 'No puedes aprobar esta solicitud todavía.');
        return;
      }
      this.toastError(resolveApiErrorMessage(error, 'Could not update partner status'));
    } finally {
      this.statusUpdating = false;
    }
  }

  async issueToken(partner: DeliveryPartner): Promise<void> {
    try {
      const response = await this.httpService
        .doPost(
          `${this.baseUrl}/partners/${encodeURIComponent(partner.id)}/device-token?deviceName=Admin%20issued`,
          {}
        )
        .toPromise();

      this.issuedToken = response?.deviceToken || '';
      this.issuedTokenPartnerName = partner.displayName;
      this.tokenDialogVisible = true;
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'Could not issue device token'));
    }
  }

  async viewOnboarding(partner: DeliveryPartner): Promise<void> {
    this.onboardingPartnerName = partner.displayName;
    this.onboardingReview = null;
    this.selectedPartner = partner;
    this.missingApprovals = [];
    this.expandedHistoryKeys.clear();
    this.historyShowAllKeys.clear();
    this.onboardingDialogVisible = true;
    await this.reloadOnboardingReview(partner.id);
  }

  closeOnboardingDialog(): void {
    this.onboardingDialogVisible = false;
    this.revokePreviewUrls();
  }

  displayRequirementGroups(): RequirementGroup[] {
    const groups = this.onboardingReview?.requirementGroups ?? [];
    return groups.filter((group) => !this.hiddenRequirementKeys.has(group.requirementKey));
  }

  artifactRequirementGroups(): RequirementGroup[] {
    return this.displayRequirementGroups().filter((group) => this.isArtifactGroup(group));
  }

  isArtifactGroup(group: RequirementGroup): boolean {
    return this.artifactRequirementKeys.has(group.requirementKey);
  }

  isPersonalInfoGroup(group: RequirementGroup): boolean {
    return group.requirementKey === 'PERSONAL_INFO';
  }

  canReviewArtifactGroup(group: RequirementGroup): boolean {
    return !!group.currentArtifact?.artifactId;
  }

  canReviewPersonalInfo(group: RequirementGroup): boolean {
    return this.isPersonalInfoGroup(group);
  }

  showReviewActions(group: RequirementGroup): boolean {
    if (this.isGroupApprovedForGate(group)) {
      return false;
    }
    return this.canReviewArtifactGroup(group) || this.canReviewPersonalInfo(group);
  }

  isTermsGroup(group: RequirementGroup): boolean {
    return group.requirementKey === 'TERMS_AGREEMENT';
  }

  canApproveApplicant(): boolean {
    return this.computeMissingApprovals().length === 0;
  }

  missingApprovalsLabel(): string {
    const missing = this.missingApprovals.length
      ? this.missingApprovals
      : this.computeMissingApprovals();
    return missing.join(', ');
  }

  computeMissingApprovals(): string[] {
    const missing = this.displayRequirementGroups()
      .filter((group) => group.required && !this.isGroupApprovedForGate(group))
      .map((group) => group.labelEs || this.requirementLabel(group.requirementKey));
    if (this.onboardingReview && !ageEligibilityEstablished(this.onboardingReview)) {
      missing.push('Mayoría de edad');
    }
    return missing;
  }

  reviewStatusClass(reviewStatus?: string): string {
    const normalized = (reviewStatus || '').toLowerCase();
    if (normalized.includes('aprobado')) {
      return 'review-status-approved';
    }
    if (normalized.includes('rechazado')) {
      return 'review-status-rejected';
    }
    if (normalized.includes('pendiente') || normalized.includes('cargado')) {
      return 'review-status-pending';
    }
    return 'review-status-empty';
  }

  requirementLabel(key: string): string {
    switch (key) {
      case 'PERSONAL_INFO':
        return 'Datos personales';
      case 'IDENTITY_DOCUMENT_FRONT':
        return 'Cédula frontal';
      case 'IDENTITY_DOCUMENT_BACK':
        return 'Cédula reverso';
      case 'IDENTITY_VIDEO':
        return 'Verificación en video';
      case 'PROFILE_PHOTO':
        return 'Foto de perfil';
      case 'TERMS_AGREEMENT':
        return 'Acuerdos';
      case 'PERMISSIONS_ACK':
        return 'Permisos';
      default:
        return key;
    }
  }

  artifactCardTitle(key: string): string {
    switch (key) {
      case 'IDENTITY_VIDEO':
        return 'Video de verificación';
      default:
        return this.requirementLabel(key);
    }
  }

  formatBytes(bytes?: number): string {
    if (!bytes || bytes <= 0) {
      return '—';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatAge(age?: number): string {
    if (age === null || age === undefined || age < 0) {
      return '—';
    }
    return `${age} años`;
  }

  isImageArtifact(artifact: OnboardingArtifact): boolean {
    const mime = this.effectiveArtifactMime(artifact);
    if (mime.startsWith('image/')) {
      return true;
    }
    const type = (artifact.artifactType || '').toUpperCase();
    if (
      type === 'PROFILE_PHOTO' ||
      type === 'IDENTITY_DOCUMENT_FRONT' ||
      type === 'IDENTITY_DOCUMENT_BACK'
    ) {
      return true;
    }
    const name = (artifact.originalFilename || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp)$/.test(name);
  }

  isVideoArtifact(artifact: OnboardingArtifact): boolean {
    const mime = this.effectiveArtifactMime(artifact);
    if (mime.startsWith('video/')) {
      return true;
    }
    if ((artifact.artifactType || '').toUpperCase() === 'IDENTITY_VIDEO') {
      return true;
    }
    const name = (artifact.originalFilename || '').toLowerCase();
    return /\.(mp4|webm|mov|m4v|3gp)$/.test(name);
  }

  previewAvailable(artifact: OnboardingArtifact): boolean {
    return !!artifact.previewUrl && !artifact.previewFailed;
  }

  previewFailed(artifact: OnboardingArtifact): boolean {
    return (
      !!artifact.previewFailed ||
      (!artifact.previewLoading &&
        !!artifact.contentAvailable &&
        (!artifact.previewUrl ||
          (!this.isImageArtifact(artifact) && !this.isVideoArtifact(artifact))))
    );
  }

  onArtifactPreviewError(artifact: OnboardingArtifact): void {
    if (this.lightboxArtifact?.artifactId === artifact.artifactId) {
      this.closeLightbox();
    }
    if (artifact.previewUrl) {
      URL.revokeObjectURL(artifact.previewUrl);
      this.previewUrls = this.previewUrls.filter((url) => url !== artifact.previewUrl);
    }
    artifact.previewFailed = true;
    artifact.previewUrl = undefined;
    if (!artifact.previewError) {
      artifact.previewError =
        'El navegador rechazó la vista previa del archivo (URL blob bloqueada o archivo dañado).';
    }
  }

  async retryArtifactPreview(artifact: OnboardingArtifact): Promise<void> {
    await this.loadSingleArtifactPreview(artifact);
    if (artifact.previewFailed && artifact.previewError) {
      this.toastError(`Vista previa: ${artifact.previewError}`);
    }
  }

  openLightbox(artifact: OnboardingArtifact, event?: Event): void {
    event?.stopPropagation();
    if (!this.previewAvailable(artifact)) {
      return;
    }
    this.lightboxArtifact = artifact;
    this.lightboxVisible = true;
  }

  closeLightbox(): void {
    this.lightboxVisible = false;
    this.lightboxArtifact = null;
  }

  openLightboxInNewTab(): void {
    const url = this.lightboxArtifact?.previewUrl;
    if (!url || typeof window === 'undefined') {
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  submittedAtLabel(): string {
    const review = this.onboardingReview;
    if (!review) {
      return '—';
    }
    const raw = review.submittedAt || review.onboarding?.submittedAt;
    if (!raw) {
      return '—';
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  activeCorrectionNote(): boolean {
    const onboarding = this.onboardingReview?.onboarding;
    const message = onboarding?.correctionMessage;
    if (!message) {
      return false;
    }
    const status = onboarding?.overallStatus;
    return status === 'NEEDS_CORRECTION';
  }

  isReviewBusy(key: string): boolean {
    return this.reviewActionKey === key;
  }

  quickReasonsFor(target: RejectTarget | null): string[] {
    if (!target) {
      return [];
    }
    const key =
      target.kind === 'artifact'
        ? target.group.requirementKey
        : target.stepId;
    return this.quickReasonsByKey[key] ?? ['Otro.'];
  }

  rejectDialogTitle(): string {
    if (!this.rejectTarget) {
      return 'Rechazar';
    }
    if (this.rejectTarget.kind === 'artifact') {
      return `Rechazar ${this.artifactCardTitle(this.rejectTarget.group.requirementKey)}`;
    }
    return 'Rechazar datos personales';
  }

  rejectSubmitLabel(): string {
    if (this.rejectTarget?.kind === 'artifact') {
      if (this.rejectTarget.group.requirementKey === 'IDENTITY_VIDEO') {
        return 'Rechazar y solicitar nuevo video';
      }
      return 'Rechazar y solicitar nueva foto';
    }
    return 'Rechazar y solicitar corrección';
  }

  openRejectArtifact(group: RequirementGroup): void {
    if (!group.currentArtifact) {
      return;
    }
    this.rejectTarget = { kind: 'artifact', group, artifact: group.currentArtifact };
    this.rejectReason = '';
    this.rejectDialogVisible = true;
  }

  openRejectPersonalInfo(group: RequirementGroup): void {
    this.rejectTarget = {
      kind: 'step',
      stepId: 'PERSONAL_INFO',
      labelEs: group.labelEs || 'Datos personales',
    };
    this.rejectReason = '';
    this.rejectDialogVisible = true;
  }

  selectQuickReason(reason: string): void {
    this.rejectReason = reason === 'Otro.' ? '' : reason;
  }

  async submitReject(): Promise<void> {
    if (!this.rejectTarget || !this.selectedPartner) {
      return;
    }
    const reason = this.rejectReason.trim();
    if (!reason) {
      this.toastError('Se requiere una razón para rechazar.');
      return;
    }

    this.rejectSubmitting = true;
    try {
      if (this.rejectTarget.kind === 'artifact') {
        await this.httpService
          .doPost(
            `${this.baseUrl}/partners/${encodeURIComponent(this.selectedPartner.id)}/artifacts/${encodeURIComponent(this.rejectTarget.artifact.artifactId!)}/review`,
            { action: 'REQUEST_CORRECTION', reason }
          )
          .toPromise();
        this.toastSuccess('Archivo rechazado. El solicitante podrá enviar uno nuevo.');
      } else {
        await this.httpService
          .doPost(
            `${this.baseUrl}/partners/${encodeURIComponent(this.selectedPartner.id)}/onboarding/steps/${encodeURIComponent(this.rejectTarget.stepId)}/review`,
            { action: 'REQUEST_CORRECTION', reason }
          )
          .toPromise();
        this.toastSuccess('Datos personales rechazados.');
      }
      this.rejectDialogVisible = false;
      await this.reloadOnboardingReview(this.selectedPartner.id);
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'No se pudo registrar el rechazo'));
    } finally {
      this.rejectSubmitting = false;
    }
  }

  async approveArtifact(group: RequirementGroup): Promise<void> {
    if (!this.selectedPartner || !group.currentArtifact?.artifactId) {
      return;
    }
    const busyKey = `artifact:${group.requirementKey}`;
    this.reviewActionKey = busyKey;
    try {
      await this.httpService
        .doPost(
          `${this.baseUrl}/partners/${encodeURIComponent(this.selectedPartner.id)}/artifacts/${encodeURIComponent(group.currentArtifact.artifactId)}/review`,
          { action: 'APPROVE' }
        )
        .toPromise();
      this.toastSuccess(`${group.labelEs || this.requirementLabel(group.requirementKey)} aprobado.`);
      await this.reloadOnboardingReview(this.selectedPartner.id);
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'No se pudo aprobar el archivo'));
    } finally {
      this.reviewActionKey = '';
    }
  }

  async approvePersonalInfo(group: RequirementGroup): Promise<void> {
    if (!this.selectedPartner) {
      return;
    }
    const busyKey = 'step:PERSONAL_INFO';
    this.reviewActionKey = busyKey;
    try {
      await this.httpService
        .doPost(
          `${this.baseUrl}/partners/${encodeURIComponent(this.selectedPartner.id)}/onboarding/steps/PERSONAL_INFO/review`,
          { action: 'APPROVE' }
        )
        .toPromise();
      this.toastSuccess('Datos personales aprobados.');
      await this.reloadOnboardingReview(this.selectedPartner.id);
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'No se pudieron aprobar los datos personales'));
    } finally {
      this.reviewActionKey = '';
    }
  }

  async downloadArtifact(artifact: OnboardingArtifact): Promise<void> {
    if (!artifact.contentAvailable || !artifact.contentPath) {
      return;
    }
    try {
      const typed = await this.fetchArtifactBlob(artifact);
      if (!typed) {
        this.toastError('No se pudo descargar el archivo.');
        return;
      }
      const url = URL.createObjectURL(typed.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = artifact.originalFilename || 'archivo';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      this.toastError(this.describePreviewFailure(error));
    }
  }

  toggleHistory(key: string): void {
    if (this.expandedHistoryKeys.has(key)) {
      this.expandedHistoryKeys.delete(key);
    } else {
      this.expandedHistoryKeys.add(key);
    }
  }

  isHistoryExpanded(key: string): boolean {
    return this.expandedHistoryKeys.has(key);
  }

  showAllHistory(key: string): void {
    this.historyShowAllKeys.add(key);
  }

  isHistoryShowAll(key: string): boolean {
    return this.historyShowAllKeys.has(key);
  }

  visibleHistoryArtifacts(group: RequirementGroup): OnboardingArtifact[] {
    const history = group.historyArtifacts ?? [];
    if (this.isHistoryShowAll(group.requirementKey)) {
      return history;
    }
    return history.slice(0, 3);
  }

  hiddenHistoryCount(group: RequirementGroup): number {
    const total = group.historyArtifacts?.length ?? 0;
    if (this.isHistoryShowAll(group.requirementKey)) {
      return 0;
    }
    return Math.max(0, total - 3);
  }

  displayAccessStatus(partner: DeliveryPartner): string {
    return partner.accessStatus || (partner.status === 'PENDING' ? 'APPLICANT' : partner.status);
  }

  statusSeverity(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status-APPROVED';
      case 'SHORTLISTED':
        return 'status-SHORTLISTED';
      case 'SUSPENDED':
        return 'status-SUSPENDED';
      case 'REJECTED':
        return 'status-REJECTED';
      default:
        return 'status-PENDING';
    }
  }

  accessSeverity(accessStatus: string): string {
    switch (accessStatus) {
      case 'APPROVED':
        return 'status-APPROVED';
      case 'SHORTLISTED':
        return 'status-SHORTLISTED';
      case 'SUSPENDED':
      case 'PAUSED':
        return 'status-SUSPENDED';
      case 'REJECTED':
      case 'DEACTIVATED':
        return 'status-REJECTED';
      default:
        return 'status-PENDING';
    }
  }

  private async reloadOnboardingReview(partnerId: string): Promise<void> {
    this.onboardingLoading = true;
    this.revokePreviewUrls();
    try {
      const response = await this.httpService
        .doGet(`${this.baseUrl}/partners/${encodeURIComponent(partnerId)}/onboarding`)
        .toPromise();
      this.onboardingReview = response ?? null;
      this.missingApprovals = this.computeMissingApprovals();
      // Show the review list immediately; media loads per item with its own spinner.
      this.onboardingLoading = false;
      await this.loadArtifactPreviews();
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'Could not load onboarding status'));
      this.onboardingDialogVisible = false;
      this.onboardingLoading = false;
    }
  }

  private async loadArtifactPreviews(): Promise<void> {
    const artifacts = this.collectPreviewArtifacts();
    const images = artifacts.filter((a) => this.isImageArtifact(a));
    const videos = artifacts.filter((a) => this.isVideoArtifact(a));
    const other = artifacts.filter((a) => !this.isImageArtifact(a) && !this.isVideoArtifact(a));
    // Photos first (parallel), then videos sequentially so one large file cannot stall the rest.
    await Promise.all(images.map((artifact) => this.loadSingleArtifactPreview(artifact)));
    for (const artifact of videos) {
      await this.loadSingleArtifactPreview(artifact);
    }
    await Promise.all(other.map((artifact) => this.loadSingleArtifactPreview(artifact)));
  }

  private async loadSingleArtifactPreview(artifact: OnboardingArtifact): Promise<void> {
    if (artifact.previewUrl) {
      URL.revokeObjectURL(artifact.previewUrl);
      this.previewUrls = this.previewUrls.filter((url) => url !== artifact.previewUrl);
    }
    artifact.previewUrl = undefined;
    artifact.previewFailed = false;
    artifact.previewError = undefined;
    if (!artifact.contentAvailable || !artifact.contentPath) {
      artifact.previewLoading = false;
      return;
    }
    artifact.previewLoading = true;
    try {
      const typed = await this.fetchArtifactBlob(artifact);
      if (!typed || typed.blob.size <= 0) {
        artifact.previewFailed = true;
        artifact.previewError = 'El archivo llegó vacío desde el servidor.';
        return;
      }
      // JSON/HTML error bodies sometimes arrive as blobs when auth fails.
      if (typed.mime.startsWith('application/json') || typed.mime.startsWith('text/html')) {
        artifact.previewFailed = true;
        artifact.previewError =
          'El servidor devolvió un error en lugar del archivo (¿sesión expirada?).';
        return;
      }
      artifact.mimeType = typed.mime;
      if (!this.isImageArtifact(artifact) && !this.isVideoArtifact(artifact)) {
        artifact.previewFailed = true;
        artifact.previewError = `Tipo no previsualizable: ${typed.mime}`;
        return;
      }
      const url = URL.createObjectURL(typed.blob);
      artifact.previewUrl = url;
      this.previewUrls.push(url);
    } catch (error) {
      artifact.previewFailed = true;
      artifact.previewError = this.describePreviewFailure(error);
    } finally {
      artifact.previewLoading = false;
    }
  }

  private async fetchArtifactBlob(
    artifact: OnboardingArtifact
  ): Promise<{ blob: Blob; mime: string } | null> {
    if (!artifact.contentPath) {
      return null;
    }
    const response = await this.httpService
      .doGetBlobResponse(`${this.apiUrl}${artifact.contentPath}`)
      .toPromise();
    if (!response?.body) {
      return null;
    }
    const headerMime = response.headers.get('Content-Type')?.split(';')[0]?.trim() || '';
    const mime = this.resolveArtifactMime(artifact, headerMime || response.body.type);
    const blob =
      response.body.type === mime
        ? response.body
        : new Blob([response.body], { type: mime });
    return { blob, mime };
  }

  private describePreviewFailure(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'No se pudo contactar la API (CORS o red). Usa https://admin.staging.tinyall.app';
      }
      if (error.status === 401 || error.status === 403) {
        return `Sin permiso para leer el archivo (HTTP ${error.status}).`;
      }
      if (error.status === 404) {
        return 'Archivo no encontrado en almacenamiento (HTTP 404).';
      }
      return `Error al cargar el archivo (HTTP ${error.status}).`;
    }
    return resolveApiErrorMessage(error as any, 'No se pudo cargar la vista previa');
  }

  /** Prefer DTO/type/filename over generic storage Content-Type (octet-stream). */
  private resolveArtifactMime(artifact: OnboardingArtifact, responseMime?: string): string {
    const candidates = [
      artifact.mimeType,
      this.mimeFromArtifactType(artifact.artifactType),
      responseMime,
    ];
    for (const candidate of candidates) {
      const mime = (candidate || '').toLowerCase().split(';')[0].trim();
      if (mime && mime !== 'application/octet-stream' && mime !== 'binary/octet-stream') {
        return mime;
      }
    }
    const fromName = this.mimeFromFilename(artifact.originalFilename);
    if (fromName) {
      return fromName;
    }
    return this.mimeFromArtifactType(artifact.artifactType) || 'application/octet-stream';
  }

  private effectiveArtifactMime(artifact: OnboardingArtifact): string {
    return this.resolveArtifactMime(artifact);
  }

  private mimeFromArtifactType(artifactType?: string): string | undefined {
    switch ((artifactType || '').toUpperCase()) {
      case 'IDENTITY_VIDEO':
        return 'video/mp4';
      case 'PROFILE_PHOTO':
      case 'IDENTITY_DOCUMENT_FRONT':
      case 'IDENTITY_DOCUMENT_BACK':
        return 'image/jpeg';
      default:
        return undefined;
    }
  }

  private mimeFromFilename(filename?: string): string | undefined {
    const name = (filename || '').toLowerCase();
    if (/\.jpe?g$/.test(name)) {
      return 'image/jpeg';
    }
    if (/\.png$/.test(name)) {
      return 'image/png';
    }
    if (/\.gif$/.test(name)) {
      return 'image/gif';
    }
    if (/\.webp$/.test(name)) {
      return 'image/webp';
    }
    if (/\.mp4$|\.m4v$/.test(name)) {
      return 'video/mp4';
    }
    if (/\.webm$/.test(name)) {
      return 'video/webm';
    }
    if (/\.mov$/.test(name)) {
      return 'video/quicktime';
    }
    return undefined;
  }

  private collectPreviewArtifacts(): OnboardingArtifact[] {
    const artifacts: OnboardingArtifact[] = [];
    const seen = new Set<string>();
    const add = (artifact?: OnboardingArtifact) => {
      if (!artifact?.artifactId || seen.has(artifact.artifactId)) {
        return;
      }
      seen.add(artifact.artifactId);
      artifacts.push(artifact);
    };

    // Current artifacts only — history stays download-only to keep the review list focused.
    for (const group of this.displayRequirementGroups()) {
      add(group.currentArtifact);
    }

    return artifacts;
  }

  isWaivedGroup(group: RequirementGroup): boolean {
    return isWaivedRequirement(group);
  }

  canWaiveGroup(group: RequirementGroup): boolean {
    return canWaiveRequirement(group);
  }

  requirementTagLabel(group: RequirementGroup): string {
    if (this.isWaivedGroup(group)) {
      return 'WAIVED';
    }
    return group.reviewStatus || group.currentArtifact?.reviewStatusLabelEs || 'No cargado';
  }

  requirementTagClass(group: RequirementGroup): string {
    if (this.isWaivedGroup(group)) {
      return 'review-status-waived';
    }
    return this.reviewStatusClass(group.reviewStatus || group.currentArtifact?.reviewStatusLabelEs);
  }

  openWaiverDialog(group: RequirementGroup, action: 'WAIVE' | 'REVOKE'): void {
    this.waiverTarget = group;
    this.waiverAction = action;
    this.waiverReason = '';
    this.waiverDialogVisible = true;
  }

  async submitRequirementWaiver(): Promise<void> {
    const group = this.waiverTarget;
    const reason = this.waiverReason.trim();
    if (!this.selectedPartner || !group || !reason) {
      this.toastError('A reason is required to waive or revoke a requirement.');
      return;
    }
    const action = this.waiverAction;
    this.waiverDialogVisible = false;
    const busyKey = `waiver:${group.requirementKey}`;
    this.reviewActionKey = busyKey;
    try {
      await this.httpService
        .doPost(
          `${this.baseUrl}/partners/${encodeURIComponent(this.selectedPartner.id)}/onboarding/requirements/${encodeURIComponent(group.requirementKey)}/waiver`,
          { action, reason }
        )
        .toPromise();
      this.toastSuccess(
        action === 'WAIVE' ? 'Requisito eximido. El acceso del courier no cambió.' : 'Exención revocada.'
      );
      await this.reloadOnboardingReview(this.selectedPartner.id);
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'No se pudo actualizar la exención'));
    } finally {
      this.reviewActionKey = '';
      this.waiverTarget = null;
    }
  }

  private isGroupApprovedForGate(group: RequirementGroup): boolean {
    return requirementSatisfiesApprovalGate(group);
  }

  private revokePreviewUrls(): void {
    this.closeLightbox();
    for (const url of this.previewUrls) {
      URL.revokeObjectURL(url);
    }
    this.previewUrls = [];
  }

  private mapLegacyStatusToAccess(status: string): string | null {
    switch (status) {
      case 'APPROVED':
        return 'APPROVED';
      case 'SHORTLISTED':
        return 'SHORTLISTED';
      case 'SUSPENDED':
        return 'SUSPENDED';
      case 'REJECTED':
        return 'REJECTED';
      case 'APPLICANT':
        return 'APPLICANT';
      default:
        return null;
    }
  }

  private emptyForm(): NewPartnerForm {
    return {
      displayName: '',
      phone: '',
      email: '',
      rateMinPrice: null,
      ratePerAdditionalKm: null,
      cashCapacityAmount: null,
    };
  }


  async viewReviews(partner: DeliveryPartner): Promise<void> {
    this.reviewsPartnerName = partner.displayName;
    this.courierReviewProfile = null;
    this.reviewsDialogVisible = true;
    this.reviewsLoading = true;
    try {
      const response = await this.httpService
        .doGet(`${this.baseUrl}/partners/${encodeURIComponent(partner.id)}/courier-profile`)
        .toPromise();
      this.courierReviewProfile = response ?? null;
    } catch (error) {
      this.toastError(resolveApiErrorMessage(error, 'Could not load courier reviews'));
      this.reviewsDialogVisible = false;
    } finally {
      this.reviewsLoading = false;
    }
  }

  formatAverageRating(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return '—';
    }
    return Number(value).toFixed(1);
  }

  private toastSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Successful', detail, life: 3000 });
  }

  private toastError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail, life: 4000 });
  }
}
